import PersonaService from 'services/personaService';
import { ContentBookmarks } from 'types/history';
import { VideoHistory } from '../HistoryService';
import { getVideoProgressPercent, isInCredits } from 'support/contentUtils';
import { VideoLaneItem } from 'services/cwData';
import { Video } from 'types/api/media';
import { getVideo } from 'services/cwService';

export default class AccountHistoryService {
  private persona: PersonaService;

  constructor(persona: PersonaService) {
    this.persona = persona;
  }

  async addOrUpdate(video: Video, saveTime: number, isAdded: boolean) {
    const progressPercentage = getVideoProgressPercent(saveTime, video);

    try {
      if (!isAdded) {
        await this.persona.upsertContinueWatchingItem({
          content_group_id: video.showSlug,
          content_type: video.seriesType,
          progress_percent: progressPercentage,
          content_id: video.guid,
        });
      } else {
        await this.persona.updateContentProgress(
          video.guid,
          progressPercentage,
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  async addNextEpisode(video: Video) {
    const next = video.nextEpisode?.guid;
    if (!next) return;
    try {
      await this.persona.upsertContinueWatchingItem({
        content_group_id: video.showSlug,
        content_type: video.seriesType,
        progress_percent: 0,
        content_id: next,
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getContinueLaneItems(
    page: number,
    pageSize?: number,
  ): Promise<VideoLaneItem[]> {
    return await this.persona.fetchAccountBookmarkItems(pageSize);
  }

  async getVideoHistory(
    showSlug: string,
    guid: string,
  ): Promise<VideoHistory | undefined> {
    const contentGroup = await this.persona.getContentGroup(showSlug);
    const item = contentGroup?.find(item => item.guid === guid);
    if (!item) return undefined;

    const isComplete = item.isComplete ?? isInCredits(item, item.progress);
    return {
      expireTime: item.expireTime,
      isComplete,
      progress: isComplete ? 0 : item.progress,
      nextEpisode: item.nextEpisode,
      lastModified: item.last_updated,
    };
  }

  async getContentBookmarks(showSlug: string): Promise<ContentBookmarks> {
    const history: ContentBookmarks = {};

    const videos = (await this.persona.getContentGroup(showSlug)) ?? [];
    videos.forEach(value => {
      history[value.guid] = {
        progress: value.progress,
        isComplete: isInCredits(value, value.progress),
      };
    });

    return history;
  }

  async getVideoProgress(
    showSlug: string,
    guid: string,
  ): Promise<number | undefined> {
    try {
      const contentGroup = await this.persona.getContentGroup(showSlug);
      const item = contentGroup?.find(item => item.guid === guid);
      return item?.progress ?? 0;
    } catch (e) {
      return undefined;
    }
  }

  async removeContinueWatchingItems(ids: string[]) {
    return this.persona.removeContinueWatchingItems(ids);
  }

  async getNextEpisode(showSlug: string): Promise<Video | undefined> {
    const videos = (await this.persona.getContentGroup(showSlug)) ?? [];
    videos.sort((a, b) => b.last_updated - a.last_updated);

    const lastWatchedItem = videos[0];
    if (
      lastWatchedItem &&
      isInCredits(lastWatchedItem, lastWatchedItem.progress)
    ) {
      // If the current episode is nearly done (reached credits), return the next episode at 0 progress.
      const nextEpisode =
        (await getVideo(lastWatchedItem.nextEpisode?.guid ?? '')) ?? undefined;
      return nextEpisode ? { ...nextEpisode, progress: 0 } : undefined;
    }

    return lastWatchedItem
      ? (await getVideo(lastWatchedItem.guid)) ?? undefined
      : undefined;
  }
}
